/**
 * Thin wrapper around `fetch` for talking to the FLIK backend.
 *
 * The base URL is configurable through REACT_APP_API_BASE_URL so the same
 * build can point at local/dev/prod.
 *
 * In development it defaults to the relative `/api` path, which the CRA dev
 * server proxies to the backend (see src/setupProxy.js). That sidesteps
 * cross-origin/CORS issues while still hitting the real endpoint:
 * /api/users/register -> localhost:8080/api/users/register.
 *
 * In a static build there is no proxy, so REACT_APP_API_BASE_URL must be an
 * absolute URL to the backend (including its `/api` context-path), e.g.
 * https://dev.api.flik.es/api — which then requires CORS on the backend.
 */
import { getToken } from '../auth/authStore';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

/**
 * Error thrown for any non-2xx response. Carries the parsed backend payload so
 * callers can surface field-level validation errors and duplicate messages
 * (the backend answers with RFC-7807 ProblemDetail bodies).
 */
export class ApiError extends Error {
  constructor(message, { status, detail, fieldErrors } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.fieldErrors = fieldErrors || {};
  }
}

/**
 * Maps the backend's English error messages to the Spanish UI copy. Falls back
 * to the raw detail for anything not explicitly translated.
 */
function translate(detail) {
  if (!detail) return 'Ha ocurrido un error inesperado.';
  if (/already taken/i.test(detail))
    return 'Ese nombre de usuario ya está en uso.';
  if (/already registered/i.test(detail))
    return 'Ese correo ya está registrado.';
  if (/code is not valid/i.test(detail)) return 'El código no es válido.';
  if (/code has expired/i.test(detail))
    return 'El código ha caducado. Pide uno nuevo.';
  if (/No active code/i.test(detail))
    return 'No hay ningún código activo. Pide uno nuevo.';
  if (/current password|contraseña actual/i.test(detail))
    return 'La contraseña actual no es correcta.';
  if (/bad credentials|invalid credentials|authentication failed/i.test(detail))
    return 'Correo o contraseña incorrectos.';
  if (/not activated|not enabled|account is disabled|user is disabled/i.test(detail))
    return 'Tu cuenta aún no está activada. Revisa tu correo.';
  if (/meta|facebook/i.test(detail))
    return 'No hemos podido validar tu cuenta de Meta. Inténtalo de nuevo.';
  if (/invalid.*(id ?)?token|token.*(invalid|expired)|google/i.test(detail))
    return 'No hemos podido validar tu cuenta de Google. Inténtalo de nuevo.';
  return detail;
}

async function request(path, { method = 'GET', body } = {}) {
  // Protected endpoints (everything under /users/me) expect a bearer token.
  // Public endpoints (register/login/activate) simply ignore it, so it is safe
  // to attach whenever a session token is present.
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    throw new ApiError(
      'No hemos podido conectar con el servidor. Inténtalo de nuevo.',
      { status: 0 }
    );
  }

  // Spring answers errors with RFC-7807 bodies whose content-type is
  // `application/problem+json`, so match any JSON-ish content type.
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('json');
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    throw new ApiError(translate(payload?.detail), {
      status: response.status,
      detail: translate(payload?.detail),
      fieldErrors: payload?.errors,
    });
  }

  return payload;
}

/** POST /users/register — creates the account and emails an activation OTP. */
export function registerUser(data) {
  return request('/users/register', { method: 'POST', body: data });
}

/**
 * POST /users/login — signs in with an email-or-username + password and
 * returns the session payload (which carries the auth token). The token field
 * name is read defensively by the caller since it may be `token`/`accessToken`/`jwt`.
 */
export function loginUser({ usernameOrEmail, password }) {
  return request('/users/login', {
    method: 'POST',
    // The backend expects the email-or-username under `identifier`.
    body: { identifier: usernameOrEmail, password },
  });
}

/**
 * POST /users/google — signs in with Google. The frontend obtains a Google
 * *ID token* via Google Identity Services and posts it here; the backend
 * verifies it (GoogleIdTokenVerifier, audience = app's client id) and answers
 * with the same session payload as a normal login (so the token is read the
 * same defensive way). The DTO field name is centralised here — if the backend
 * expects `credential`/`token` instead of `idToken`, change it in one place.
 */
export function loginWithGoogle({ idToken }) {
  return request('/users/google', {
    method: 'POST',
    body: { idToken },
  });
}

/**
 * POST /users/meta — signs in with Meta (Facebook Login). The frontend obtains
 * a Meta *user access token* via the Facebook JS SDK and posts it here; the
 * backend verifies it against Meta's Graph API and answers with the same
 * session payload as a normal login (token read the same defensive way). The
 * DTO field name is centralised here — if the backend expects `token`/`code`
 * instead of `accessToken`, change it in one place. Same for the path if the
 * controller maps `/users/facebook`.
 */
export function loginWithMeta({ accessToken }) {
  return request('/users/meta', {
    method: 'POST',
    body: { accessToken },
  });
}

/** POST /users/activate — confirms the account with the emailed code. */
export function activateAccount({ email, code }) {
  return request('/users/activate', {
    method: 'POST',
    body: { email, code },
  });
}

/** POST /users/activate/resend — issues and emails a fresh OTP. */
export function resendActivationCode({ email }) {
  return request('/users/activate/resend', {
    method: 'POST',
    body: { email },
  });
}

/* ------------------------------------------------------------------------- *
 *  Profile ("/users/me") — the signed-in user's own account.
 *  All of these require a valid session token (attached automatically above).
 * ------------------------------------------------------------------------- */

/**
 * GET /users/me — the signed-in user's profile (UserResponse). Companion to the
 * PUT below; used to populate the profile page on load.
 */
export function getMyProfile() {
  return request('/users/me');
}

/**
 * PUT /users/me — partial profile update. Every field is optional; only the
 * ones provided are changed. Accepts { name, surname, birthday, genre,
 * biography } and answers 200 with the updated UserResponse.
 */
export function updateProfile(data) {
  return request('/users/me', { method: 'PUT', body: data });
}

/** GET /users/me/preferences — { publicProfile, showRoutes, notificationsEnabled }. */
export function getPreferences() {
  return request('/users/me/preferences');
}

/**
 * PUT /users/me/preferences — partial preferences update (each flag is a
 * nullable Boolean; send only what changes). Answers 200 with the full
 * PreferencesResponse.
 */
export function updatePreferences(data) {
  return request('/users/me/preferences', { method: 'PUT', body: data });
}

/**
 * POST /users/me/password/change — begins a password change. Validates the
 * current password and emails a confirmation code. Answers 202 (no body);
 * finish with confirmPasswordChange.
 */
export function changePassword({ currentPassword, newPassword }) {
  return request('/users/me/password/change', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
}

/** POST /users/me/password/confirm — confirms the change with the emailed code (204). */
export function confirmPasswordChange({ code }) {
  return request('/users/me/password/confirm', {
    method: 'POST',
    body: { code },
  });
}

/**
 * POST /users/me/delete — begins account deletion. Emails a confirmation code.
 * Answers 202 (no body); finish with confirmAccountDeletion.
 */
export function requestAccountDeletion() {
  return request('/users/me/delete', { method: 'POST' });
}

/** POST /users/me/delete/confirm — confirms deletion with the emailed code (204). */
export function confirmAccountDeletion({ code }) {
  return request('/users/me/delete/confirm', {
    method: 'POST',
    body: { code },
  });
}

/* ------------------------------------------------------------------------- *
 *  Admin ("/admin/users") — administration dashboard. All require an ADMIN
 *  session token (attached automatically above).
 * ------------------------------------------------------------------------- */

/**
 * GET /admin/users — paginated user list. Optional `search` (matches username,
 * name and email) and `role` (user type) filters; `page`/`size` drive Spring
 * pagination. Answers a Spring `Page` ({ content, totalElements, totalPages,
 * number, size, ... }).
 */
export function getAdminUsers({ search, role, page = 0, size = 10 } = {}) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (role) params.set('role', role);
  params.set('page', String(page));
  params.set('size', String(size));
  return request(`/admin/users?${params.toString()}`);
}

/** GET /admin/users/count — total number of registered users. */
export function getAdminUsersCount() {
  return request('/admin/users/count');
}

/** GET /admin/users/premium/count — number of premium users. */
export function getAdminPremiumCount() {
  return request('/admin/users/premium/count');
}

/**
 * GET /admin/users/registrations — registrations for a given `year`, restricted
 * to the requested `months` (an array of 1-based month numbers, sent as repeated
 * `month` params).
 */
export function getAdminRegistrations({ year, months } = {}) {
  const params = new URLSearchParams();
  if (year) params.set('year', String(year));
  (months || []).forEach((m) => params.append('month', String(m)));
  return request(`/admin/users/registrations?${params.toString()}`);
}
